import React, {FC} from 'react';
import PhoneInput from 'react-phone-input-2';
import reactPhoneInputStyles from 'react-phone-input-2/lib/style.css';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useCss} from '../../../../libs/shared/ui/src/css/cssContext';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {ThemeContextType} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Constants.
 */

const defaultCountry = 'US';

/*
 * Props.
 */

interface GlobalPhoneNumberInputProps {
  hasError: boolean;
  phoneNumber: string;
  phoneCountryCode: string;
  label?: React.ReactNode;
  onChange: (phone: string, countryCode: string) => void;
  tabIndex: number;
  onBlur?: Handler;
}

/*
 * Style.
 */

const StyledTextLabel = styled.label`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade70};
  grid-area: title;
`;

const PhoneInputStyles = () => {
  useCss(reactPhoneInputStyles);

  return null;
};

interface PhoneInputWrapperProps {
  $hasError: boolean;
}

/** Container div used to override the third party phone input style. */
const StyledPhoneInputWrapperDiv = styled.div<PhoneInputWrapperProps>`
  margin-top: 8px;
  margin-bottom: 12px;
  .react-tel-input {
    height: 30px;
    border: 2px solid;
    border-color: ${(p) => buildInputBackgroundColor(p)};
    border-radius: 4px;

    &:focus-within {
      border-color: ${(p) => (p.$hasError ? p.theme.palette.red.shade40 : p.theme.palette.blue.shade40)};
      background-color: ${(p) => p.theme.greys.white};

      .form-control {
        border-color: ${(p) => p.theme.greys.shade20};
        background-color: ${(p) => p.theme.greys.white};
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }

      .flag-dropdown {
        background-color: ${(p) => p.theme.greys.white};
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }

      .selected-flag {
        background-color: ${(p) => p.theme.greys.white};
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }
    }

    .form-control {
      font-weight: ${fontWeights.normal};
      height: 26px;
      width: calc(100% - 52px);
      background-color: ${buildInputBackgroundColor};
      border: none;
      border-left: 2px solid ${(p) => p.theme.greys.white};
      margin-left: 52px;
      border-radius: 0px;
      padding-left: 12px;
      cursor: text;
    }

    .flag-dropdown {
      height: 26px;
      width: 52px;
      border: none;
      background-color: ${buildInputBackgroundColor};

      .open {
        background: ${(p) => p.theme.greys.shade20};
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        border-top-right-radius: 0px;
        border-bottom-right-radius: 0px;
      }
    }

    .selected-flag {
      padding: 0 0 0 15px;
      border-radius: 0px;
      width: 52px;
      background-color: ${buildInputBackgroundColor};
      &:hover {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }
    }

    .country-list {
      background: ${(p) => p.theme.greys.white};
      ${(p) => boxShadow(p.theme, `0px 4px 16px rgba(0, 0, 0, 0.08)`)};
      border-radius: 4px;
      padding: 0px;
      ${fontWeights.normal};
      margin: 10px 0 10px -3px;
      max-height: 160px;

      .search {
        background-color: ${(p) => p.theme.greys.white};
        padding: 8px;
      }

      .search-emoji {
        display: none;
      }

      .search-box {
        border: 2px solid ${(p) => p.theme.palette.blue.shade40};
        border-radius: 4px;
        ${fontWeights.normal};
        height: 30px;
        padding-left: 11px;
        padding-top: 5px;
        padding-right: 5px;
        margin: 0px;
        width: 100%;
        cursor: text;
      }

      .country {
        padding: 5px 12px;
        font-weight: ${fontWeights.medium};

        &:hover {
          background-color: ${(p) => p.theme.alphas.gray10};
        }

        .dial-code {
          color: inherit;
        }
      }

      .country.highlight {
        font-weight: ${fontWeights.bold};
        background-color: inherit;
      }
    }
  }
`;

function buildInputBackgroundColor(props: PhoneInputWrapperProps & ThemeContextType) {
  return props.$hasError ? props.theme.palette.red.shade20 : props.theme.greys.shade20;
}

/*
 * Component.
 */

export const GlobalPhoneNumberInput: FC<GlobalPhoneNumberInputProps> = (props) => (
  <div>
    {maybeRenderLabel(props)}
    <StyledPhoneInputWrapperDiv $hasError={props.hasError}>
      <PhoneInputStyles />
      <PhoneInput
        autoFormat
        country={defaultCountry.toLowerCase()}
        value={props.phoneNumber}
        onChange={props.onChange}
        onBlur={props.onBlur}
        preferredCountries={['us', 'fr', 'uk', 'ca', 'in', 'au', 'de', 'nl', 'bra']}
        preserveOrder={['preferredCountries']}
        inputProps={{tabIndex: props.tabIndex}}
        enableSearch
        disableSearchIcon
        searchPlaceholder="Search for your country"
      />
    </StyledPhoneInputWrapperDiv>
  </div>
);

/*
 * Helpers.
 */

function maybeRenderLabel(props: GlobalPhoneNumberInputProps) {
  if (!props.label) {
    return null;
  }

  return <StyledTextLabel>{props.label}</StyledTextLabel>;
}
